import './App.css';
import './Survey.css';
import React, { useRef, useState } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import SelectComponent from './SelectComponent';
import { sendLinkToApp } from './nativeBridge';

const getUrlContext = () => {
  const params = new URLSearchParams(window.location.search);
  const rawProductType = params.get('productType');
  const parsedProductType = rawProductType === null ? null : parseInt(rawProductType, 10);

  return {
    token: params.get('token') || '',
    productType: Number.isNaN(parsedProductType) ? null : parsedProductType,
    email: params.get('email') || '',
    phone: params.get('phone') || '',
  };
};

const StarRating = ({ value, onChange }) => {
  return (
    <div className="survey-star-rating">
      {[1, 2, 3, 4, 5].map((star) => {
        const isSelected = star <= value;
        return (
          <span
            key={star}
            className="survey-star-item"
            onClick={() => onChange(star)}
          >
            <svg
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill={isSelected ? '#F05627' : 'none'}
              stroke="#F05627"
              strokeWidth="1"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
          </span>
        );
      })}
    </div>
  );
};

function Survey({ onRequestComplete }) {
  const API_URL_TEST = 'https://api-test.aihear.io';
  const API_URL = 'https://api-prod.aihear.io';
  const urlContext = getUrlContext();

  const [isDisabled, setIsDisabled] = useState(false);
  const inputRefName = useRef(null);
  const inputRefPhone = useRef(null);
  const inputRefEmail = useRef(null);
  const inputRefFavorite = useRef(null);
  const inputRefNeed = useRef(null);

  const [channel, setChannel] = useState('Amazon');
  const [hearingAidRating, setHearingAidRating] = useState(0);
  const [favorite, setFavorite] = useState('');
  const [need, setNeed] = useState('');
  const [recommendRating, setRecommendRating] = useState(0);
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState(urlContext.phone);
  const [email, setEmail] = useState(urlContext.email);

  const handleToggle = () => setIsDisabled(!isDisabled);

  const sendFeedback = async (callback) => {
    let httpRequest = new XMLHttpRequest();
    httpRequest.open('POST', `${API_URL}/info/add`, true);
    httpRequest.setRequestHeader('Content-Type', 'application/json;charset=UTF-8');

    const productType = urlContext.productType ?? 0;
    var data = JSON.stringify({
      name: name,
      email: email,
      phoneNumber: phoneNumber,
      orderSource: channel,
      favoriteFt: favorite,
      rating: hearingAidRating,
      improvements: need,
      nps: (recommendRating * 2).toString(),
      productType: productType,
    });

    httpRequest.send(data);

    httpRequest.onreadystatechange = function () {
      if (httpRequest.readyState === 4 && httpRequest.status === 200) {
        let json = httpRequest.responseText;
        callback(json);
      }
    };
  };

  const callbackSubmit = (json) => {
    try {
      let jsonObject = window.JSON.parse(json);
      if (jsonObject.code === 200) {
        onRequestComplete();
      } else {
        console.log('send feedback failed: ', jsonObject.message);
        toast.error('Feedback submission failed: ' + jsonObject.message, {
          position: 'top-center',
        });
        handleToggle();
      }
    } catch (error) {
      console.error('Error submitting feedback: ', error);
      toast.error('Error submitting feedback.', {
        position: 'top-center',
      });
      handleToggle();
    }
  };

  const isFormValid = hearingAidRating > 0 && recommendRating > 0 && name !== '' && phoneNumber !== '' && favorite !== '' && need !== '';

  const handleSubmit = () => {
    if (hearingAidRating === 0) {
      toast.error('Please rate your hearing aids.', { position: 'top-center' });
      return;
    }
    if (recommendRating === 0) {
      toast.error('Please provide a recommendation rating.', { position: 'top-center' });
      return;
    }
    if (favorite === '') {
      toast.error('Please enter your favorite feature.', { position: 'top-center' });
      inputRefFavorite.current.focus();
      return;
    }
    if (need === '') {
      toast.error('Please enter your suggestions for improvement.', { position: 'top-center' });
      inputRefNeed.current.focus();
      return;
    }
    if (name === '') {
      toast.error('Please enter a valid name.', { position: 'top-center' });
      inputRefName.current.focus();
      return;
    }
    if (phoneNumber === '') {
      toast.error('Please enter a valid phone number.', { position: 'top-center' });
      inputRefPhone.current.focus();
      return;
    }
    if (email !== '') {
      const isValidEmail = /^([A-Za-z0-9_\-.])+@([A-Za-z0-9_\-.])+\.([A-Za-z]{2,4})$/.test(
        String(email).toLowerCase()
      );
      if (!isValidEmail) {
        toast.error('The email format is incorrect.', { position: 'top-center' });
        inputRefEmail.current.focus();
        return;
      }
    }

    handleToggle();
    sendFeedback(callbackSubmit);
  };

  return (
    <div className="survey-page">
      {/* Navigation Bar */}
      {/* <div className="survey-navbar">
        <span className="survey-navbar-title">Write a Product Review</span>
      </div> */}

      {/* Scrollable Content */}
      <div className="survey-scroll-content">
        {/* White Section - Questionnaire */}
        <div className="survey-section-white">
          <div className="survey-section-inner">
            {/* Intro Text */}
            <div className="survey-intro">
              <span className="survey-intro-bold">OVER 1.5 BILLION</span>{' '}
              people worldwide experience hearing loss, yet 83% go without help. At ELEHEAR, we
              believe quality hearing support should be accessible to everyone. Your survey feedback
              helps us make this vision a reality. Thank you for being part of this meaningful change.
            </div>

            {/* Q1: Purchase Channel */}
            <div className="survey-field-group">
              <label className="survey-label">Where did you purchase our product?</label>
              <div className="survey-select-wrapper survey-select-wrapper-main-aligned">
                <SelectComponent onSelectChange={setChannel} />
              </div>
            </div>

            {/* Q2: Hearing Aid Rating */}
            <div className="survey-field-group">
              <label className="survey-label">How would you rate your hearing aids?</label>
              <StarRating value={hearingAidRating} onChange={setHearingAidRating} />
            </div>

            {/* Q3: Favorite Feature */}
            <div className="survey-field-group">
              <label className="survey-label">What's your favorite feature of the product?</label>
              <div className="survey-input-wrapper">
                <input
                  ref={inputRefFavorite}
                  className="survey-input"
                  placeholder="Please enter your favorite feature"
                  value={favorite}
                  onChange={(e) => setFavorite(e.target.value)}
                />
              </div>
            </div>

            {/* Q4: Improvements */}
            <div className="survey-field-group">
              <label className="survey-label">
                What improvements do you think the product still needs?
              </label>
              <div className="survey-textarea-wrapper">
                <textarea
                  ref={inputRefNeed}
                  className="survey-textarea"
                  placeholder="Please enter your suggestions for improvement"
                  value={need}
                  onChange={(e) => setNeed(e.target.value)}
                />
              </div>
            </div>

            {/* Q5: NPS */}
            <div className="survey-field-group">
              <label className="survey-label">
                How likely are you to recommend our product to friends and family?
              </label>
              <StarRating value={recommendRating} onChange={setRecommendRating} />
            </div>
          </div>
        </div>

        {/* Gray Section - User Info */}
        <div className="survey-section-gray">
          <div className="survey-section-inner">
            <div className="survey-info-title">Please fill in the info below</div>

            {/* User Name */}
            <div className="survey-field-group">
              <label className="survey-label">User Name</label>
              <div className="survey-input-wrapper survey-input-white">
                <input
                  ref={inputRefName}
                  className="survey-input"
                  placeholder="Please enter your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
            </div>

            {/* Phone Number */}
            <div className="survey-field-group">
              <label className="survey-label">Phone Number</label>
              <div className="survey-input-wrapper survey-input-white">
                <input
                  ref={inputRefPhone}
                  className="survey-input"
                  placeholder="Please enter mobile number"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                />
              </div>
            </div>

            {/* Email Address */}
            <div className="survey-field-group">
              <label className="survey-label">Email Address (Optional)</label>
              <div className="survey-input-wrapper survey-input-white">
                <input
                  ref={inputRefEmail}
                  className="survey-input"
                  placeholder="Please enter email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>

            {/* Privacy & Submit */}
            <div className="survey-submit-section">
              <p className="survey-privacy">
                By submitting, you agree to our{' '}
                <a
                  href="https://api-prod.aihear.io/privacy_policy.html"
                  rel="noopener noreferrer"
                  className="survey-privacy-link"
                  onClick={(e) => {
                    e.preventDefault();
                    sendLinkToApp("https://api-prod.aihear.io/privacy_policy.html");
                  }}
                >
                  Privacy Policy
                </a>
                . We will strictly protect your personal information.
              </p>
              <button
                disabled={isDisabled}
                className={`survey-submit-btn ${isFormValid && !isDisabled ? 'active' : ''}`}
                onClick={handleSubmit}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      </div>

      <Toaster />
    </div>
  );
}

export default Survey;
